import { init as initEngagement } from '@amplitude/engagement-browser'

const AMPLITUDE_HTTP_ENDPOINT = 'https://api.eu.amplitude.com/2/httpapi'
const RATING_PROPERTY = '[Guides-Surveys] Survey Response Number'
const SURVEY_SUBMITTED = '[Guides-Surveys] Survey Submitted'
const AUTO_REFRESH_SECONDS = 3600
// Turbolinks replaces document.body, so the SDK must mount outside it.
const MOUNT_ELEMENT_ID = 'amplitude-engagement-root'
// Amplitude's own inactivity window, so sessions here line up with the rest of the project.
const SESSION_TIMEOUT_MS = 30 * 60 * 1000
const SESSION_STORAGE_KEY = 'amplitudeSession'

let config = null
let lastRating = null

export function initAmplitudeEngagement() {
  if (window.__amplitudeEngagement) return

  config = readConfig()
  if (!config) return

  window.__amplitudeEngagement = true

  try {
    initEngagement(config.apiKey, {
      serverZone: 'EU',
      autoRefreshIntervalSeconds: AUTO_REFRESH_SECONDS,
      options: { mountElementId: MOUNT_ELEMENT_ID }
    })

    document.getElementById('engagement-sdk-bundle')
      ?.addEventListener('error', () => console.warn('[Amplitude] SDK bundle failed to load'))

    if (typeof window.engagement?.boot !== 'function') {
      console.warn('[Amplitude] window.engagement is not the Guides & Surveys SDK')
      return
    }

    window.engagement.boot({
      user: {
        user_id: config.userId,
        user_properties: { is_trial: config.isTrial, days_on_platform: config.daysOnPlatform }
      },
      integrations: [{ track: handleAmplitudeEvent }]
    })
      // Forwarding before boot resolves drops the occurrence for good.
      .then(bindSurveyTriggers)
      .catch((error) => console.warn('[Amplitude] boot failed, survey triggers not bound:', error))
  } catch (error) {
    console.warn('[Amplitude] SDK failed to load:', error)
  }
}

function readConfig() {
  const data = document.getElementById(MOUNT_ELEMENT_ID)?.dataset
  if (!data?.amplitudeApiKey || !data.amplitudeUserId) return null

  return {
    apiKey: data.amplitudeApiKey,
    userId: data.amplitudeUserId,
    isTrial: data.amplitudeIsTrial === 'true',
    daysOnPlatform: Number(data.amplitudeDaysOnPlatform)
  }
}

// 'survey-trigger' must match the trigger name configured on the Amplitude survey.
function bindSurveyTriggers() {
  const forward = () => window.engagement.forwardEvent('survey-trigger')

  window.addEventListener('vessel:created', forward)
  window.addEventListener('snp:vesselAdded', forward)
  window.addEventListener('vessel-form:disableSubmit', forward)
  window.addEventListener('snp:afterCalculate', forward)
}

// Runs inside the SDK's render pass, so a throw here would abort what it was drawing.
function handleAmplitudeEvent(event) {
  if (!event || typeof event.event_type !== 'string') return

  try {
    shipEventToAmplitude(event)

    const number = (event.event_properties || {})[RATING_PROPERTY]
    if (typeof number === 'number') lastRating = number

    if (event.event_type === SURVEY_SUBMITTED && lastRating !== null) {
      window.dispatchEvent(new CustomEvent('amplitude:survey-submitted', { detail: { rating: lastRating } }))
      lastRating = null
    }
  } catch (error) {
    console.warn('[Amplitude] event handling failed:', error)
  }
}

// session_id must be the session's start timestamp, or events file as sessionless (-1).
function currentSessionId() {
  const now = Date.now()
  let stored = null

  try {
    stored = JSON.parse(window.localStorage.getItem(SESSION_STORAGE_KEY))
  } catch (error) {
    stored = null
  }

  const expired = !stored || !stored.id || now - stored.lastEventTime > SESSION_TIMEOUT_MS
  const id = expired ? now : stored.id

  try {
    window.localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify({ id, lastEventTime: now }))
  } catch (error) {
    // Private browsing rejects writes; the id above still tags this event.
  }

  return id
}

// The analytics-browser SDK pulls in web-vitals and breaks the Webpack 4 build.
function shipEventToAmplitude(event) {
  if (!config) return

  fetch(AMPLITUDE_HTTP_ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      api_key: config.apiKey,
      events: [{
        user_id: config.userId,
        session_id: currentSessionId(),
        event_type: event.event_type,
        event_properties: event.event_properties || {},
        user_properties: { is_trial: config.isTrial, days_on_platform: config.daysOnPlatform },
        time: Date.now()
      }]
    }),
    keepalive: true
  }).catch(() => {})
}
